import { createContext, useContext } from 'react';

// Supported languages
export type SupportedLanguage = 'nb' | 'sv' | 'en';

// Translation interface
export interface Translation {
  [key: string]: string | Translation;
}

// Language resources
export interface LanguageResource {
  [namespace: string]: Translation;
}

// Translation context type
export interface I18nContextType {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  t: (key: string, params?: Record<string, string | number | null | undefined>, defaultText?: string) => string;
  isLoading: boolean;
}

// Default translations (fallback)
export const defaultTranslations: Record<SupportedLanguage, LanguageResource> = {
  nb: {},
  sv: {},
  en: {}
};

// Translation context
export const I18nContext = createContext<I18nContextType | null>(null);

// Hook for using translations
export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within I18nProvider');
  }
  return context;
};

// Translation helper functions
export class TranslationHelper {
  /**
   * Get nested value from object using dot notation
   */
  static getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
  }

  /**
   * Replace placeholders in translation string
   * Example: "Hello {name}" with params {name: "John"} returns "Hello John"
   */
  static replacePlaceholders(text: string, params?: Record<string, string | number | null | undefined>): string {
    if (!params) return text;

    return Object.entries(params).reduce((result, [key, value]) => {
      const placeholder = `{${key}}`;
      // Use split/join for safe string replacement instead of regex
      // null/undefined render as empty string (avoids "undefined" in UI)
      return result.split(placeholder).join(String(value ?? ''));
    }, text);
  }

  /**
   * Create translation function for specific language and resources
   */
  static createTranslationFunction(
    language: SupportedLanguage,
    resources: LanguageResource,
    fallbackResources?: LanguageResource
  ) {
    return (key: string, params?: Record<string, string | number | null | undefined>): string => {
      // Try to get translation from current language resources
      let translation = TranslationHelper.getNestedValue(resources, key);

      // If not found, try fallback resources
      if (translation === undefined && fallbackResources) {
        translation = TranslationHelper.getNestedValue(fallbackResources, key);
      }

      // If still not found, return key itself
      if (translation === undefined) {
        console.warn(`Translation missing for key: ${key} (language: ${language})`);
        return key;
      }

      // If translation is not a string, return key
      if (typeof translation !== 'string') {
        console.warn(`Translation is not a string for key: ${key} (language: ${language})`);
        return key;
      }

      // Replace placeholders
      return TranslationHelper.replacePlaceholders(translation, params);
    };
  }
}

// Language configuration
export const LANGUAGE_CONFIG = {
  defaultLanguage: 'nb' as SupportedLanguage,
  supportedLanguages: ['nb', 'sv', 'en'] as SupportedLanguage[],
  storageKey: 'bizclick_wms_language',
  sessionStorageKey: 'bizclick_wms_translations',
  buildVersionKey: 'bizclick_wms_build_version'
};

// Language detection
export class LanguageDetector {
  /**
   * Get preferred language from various sources
   */
  static getPreferredLanguage(): SupportedLanguage {
    // 1. Check localStorage for saved preference
    const stored = localStorage.getItem(LANGUAGE_CONFIG.storageKey);
    if (stored && LANGUAGE_CONFIG.supportedLanguages.includes(stored as SupportedLanguage)) {
      return stored as SupportedLanguage;
    }

    // 2. Check browser language
    const browserLang = navigator.language.split('-')[0];
    if (LANGUAGE_CONFIG.supportedLanguages.includes(browserLang as SupportedLanguage)) {
      return browserLang as SupportedLanguage;
    }

    // 3. Return default
    return LANGUAGE_CONFIG.defaultLanguage;
  }

  /**
   * Save language preference
   */
  static saveLanguagePreference(language: SupportedLanguage): void {
    localStorage.setItem(LANGUAGE_CONFIG.storageKey, language);
  }
}

// Translation cache
export class TranslationCache {
  /**
   * Get cached translations for language
   */
  static getCachedTranslations(language: SupportedLanguage): LanguageResource | null {
    try {
      const cached = sessionStorage.getItem(`${LANGUAGE_CONFIG.sessionStorageKey}_${language}`);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.warn('Failed to get cached translations:', error);
      return null;
    }
  }

  /**
   * Cache translations for language
   */
  static cacheTranslations(language: SupportedLanguage, translations: LanguageResource): void {
    try {
      sessionStorage.setItem(
        `${LANGUAGE_CONFIG.sessionStorageKey}_${language}`,
        JSON.stringify(translations)
      );
    } catch (error) {
      console.warn('Failed to cache translations:', error);
    }
  }

  /**
   * Clear cached translations
   */
  static clearCache(): void {
    LANGUAGE_CONFIG.supportedLanguages.forEach(lang => {
      sessionStorage.removeItem(`${LANGUAGE_CONFIG.sessionStorageKey}_${lang}`);
    });
  }

  /**
   * Invalidate cache if the app's build version has changed.
   * Fetches /version.json (written at build time) and compares to the version
   * stored when the cache was last populated. Returns true if cache was cleared.
   */
  static async invalidateOnBuildChange(): Promise<boolean> {
    try {
      const res = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
      if (!res.ok) return false;
      const data = await res.json();
      const currentVersion: string | undefined = data?.buildTime;
      if (!currentVersion) return false;

      const stored = localStorage.getItem(LANGUAGE_CONFIG.buildVersionKey);
      if (stored === currentVersion) return false;

      TranslationCache.clearCache();
      localStorage.setItem(LANGUAGE_CONFIG.buildVersionKey, currentVersion);
      return true;
    } catch {
      return false;
    }
  }
}